import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Apple, OpenInNew } from "@mui/icons-material";

interface PublicStudyInfo {
  name: string;
  description: string | null;
}

const APP_STORE_URL =
  "https://apps.apple.com/us/app/studysync-snyder-lab/id6756796122";

const AppEnrollmentRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const studyCode = searchParams.get("studyCode")?.trim().toUpperCase() || null;
  const appDeepLink = studyCode
    ? `studysyncs://app/enroll?studyCode=${encodeURIComponent(studyCode)}`
    : null;

  const [studyInfo, setStudyInfo] = useState<PublicStudyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStudyInfo = async () => {
      if (!studyCode) {
        setError("Missing study code in this enrollment link.");
        setLoading(false);
        return;
      }

      try {
        const apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:3002";
        const response = await fetch(
          `${apiBaseUrl}/api/studies/public-info/${encodeURIComponent(studyCode)}`
        );
        const payload = await response.json();

        if (!response.ok || payload?.success === false) {
          setError(payload?.error || "Unable to load study information.");
          return;
        }

        setStudyInfo({
          name: payload?.name || payload?.data?.name || "Research Study",
          description: payload?.description || payload?.data?.description || null,
        });
      } catch (err) {
        setError("Unable to load study information.");
      } finally {
        setLoading(false);
      }
    };

    fetchStudyInfo();
  }, [studyCode]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading study details...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
          Join Study with StudySync
        </Typography>

        {studyInfo && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="primary">
              {studyInfo.name}
            </Typography>
            {studyInfo.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {studyInfo.description}
              </Typography>
            )}
          </Box>
        )}

        {studyCode && (
          <Paper
            variant="outlined"
            sx={{ p: 2, mb: 3, backgroundColor: "grey.50" }}
          >
            <Typography variant="body2" color="text.secondary">
              Study Code
            </Typography>
            <Typography variant="h5" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
              {studyCode}
            </Typography>
          </Paper>
        )}

        {error && (
          <Alert severity="warning" sx={{ mb: 3, textAlign: "left" }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" sx={{ mb: 3 }}>
          Download the StudySync iOS app to enroll and securely share Apple
          Health (HealthKit) data for this study.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            gap: 2,
            mb: 3,
          }}
        >
          {appDeepLink && (
            <Button
              variant="contained"
              size="large"
              startIcon={<OpenInNew />}
              href={appDeepLink}
            >
              Open in app
            </Button>
          )}

          <Button
            variant={appDeepLink ? "outlined" : "contained"}
            size="large"
            startIcon={<Apple />}
            href={APP_STORE_URL}
            target="_blank"
            rel="noreferrer"
          >
            Download on App Store
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {appDeepLink
            ? "If StudySync is already installed, tap Open in app. Otherwise, install the app and enter the study code shown above."
            : "Install the app, then enter the study code shown above to continue enrollment."}
        </Typography>
      </Paper>
    </Container>
  );
};

export default AppEnrollmentRedirect;
